import { Badge, Button, Card, LoadingContainer } from '@/components/ui';
import { useAsesoriasHorasPorRechazo } from '@/hooks/useSysadmin';
import { formatHorasDecimales } from '@/utils/formatDuration';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';

/**
 * `Asesoria.horas` se suma apenas llega cada audio (webhook), antes de cualquier revisión, y solo
 * se descuenta cuando el audio se oculta por reasignación de asesor (ver
 * AudiosEliminadosReasignacionPanel). Si un audio queda RECHAZADO sin reasignación, sus horas
 * quedan sumadas para siempre una vez FINALIZADA la asesoría. Este panel detecta esos casos y
 * permite corregir cada asesoría puntual, propagando el ajuste al acumulado de la empresa.
 */
export function AsesoriasHorasPorRechazoPanel() {
  const { asesorias, isLoading, error, isCorrigiendo, corregir } = useAsesoriasHorasPorRechazo();
  const [corrigiendoId, setCorrigiendoId] = useState<number | null>(null);
  const [corregidas, setCorregidas] = useState(0);

  const handleCorregir = async (asesoriaId: number) => {
    setCorrigiendoId(asesoriaId);
    try {
      await corregir(asesoriaId);
      setCorregidas((prev) => prev + 1);
    } catch (err: any) {
      alert(err.message || 'Error al corregir horas de la asesoría');
    } finally {
      setCorrigiendoId(null);
    }
  };

  if (isLoading && asesorias.length === 0) return <LoadingContainer text="Buscando asesorías con horas por rechazo..." />;

  if (error) {
    return (
      <Card className="p-4 bg-red-50 border-red-100 text-red-700">
        <p className="text-sm font-medium">Error al cargar: {error}</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="p-4 bg-amber-50/80 border border-amber-200 rounded-xl flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-bold text-amber-900 text-sm">
            {asesorias.length} asesoría{asesorias.length === 1 ? '' : 's'} FINALIZADA con horas infladas por audios rechazados
          </h3>
          <p className="text-xs text-amber-800 mt-0.5">
            Un audio rechazado sin reasignación de asesor nunca descuenta sus horas — si la asesoría ya está FINALIZADA, ese valor queda
            fijo para siempre. Corregir resuma solo los audios vigentes y propaga el ajuste al acumulado de la empresa.
          </p>
        </div>
      </div>

      {corregidas > 0 && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-sm text-emerald-800">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {corregidas} asesoría{corregidas === 1 ? '' : 's'} corregida{corregidas === 1 ? '' : 's'}.
        </div>
      )}

      {asesorias.length === 0 ? (
        <Card className="p-8 text-center text-gray-500 text-sm">No hay asesorías FINALIZADA con horas infladas por rechazo.</Card>
      ) : (
        <Card className="overflow-hidden border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Empresa</th>
                  <th className="px-4 py-3">Asesoría</th>
                  <th className="px-4 py-3">Audios rechazados</th>
                  <th className="px-4 py-3">Horas actual</th>
                  <th className="px-4 py-3">Horas correctas</th>
                  <th className="px-4 py-3 text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {asesorias.map((a) => (
                  <tr key={a.asesoriaId}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{a.empresaNombre}</div>
                      <div className="text-xs text-gray-400 font-mono">{a.empresaId}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className="bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">#{a.asesoriaId}</Badge>
                    </td>
                    <td className="px-4 py-3">{a.audiosRechazados}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-red-600 font-medium">{formatHorasDecimales(a.horasActual)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-emerald-700 font-medium">{formatHorasDecimales(a.horasCalculadas)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        size="sm"
                        onClick={() => handleCorregir(a.asesoriaId)}
                        disabled={isCorrigiendo}
                        className="bg-amber-600 hover:bg-amber-700 text-white text-xs py-1.5 px-3"
                      >
                        {corrigiendoId === a.asesoriaId ? 'Corrigiendo...' : 'Corregir'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
